import { useState, useEffect } from 'react'
import { isNull } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { ReactComponent as UploadCloud01 } from '@/assets/images/icons/upload-cloud-01.svg'
import { Image } from '@/ui'
import CropModal from './CropModal'
import { readFileAsync } from '@/helpers/general-helpers'
import {
  ImageUploadWrapper,
  DropContainer,
  DropLabel,
  EditIconWrapper,
  EditIcon,
} from './Styles'

const getFilesFromEvent = (event: DragEvent) => {
  let items = null
  if ('dataTransfer' in event) {
    const dt = event.dataTransfer
    const target = event.target as HTMLInputElement
    if (dt) {
      // NOTE: Only the 'drop' event has access to DataTransfer.files, otherwise it will always be empty
      if ('files' in dt && dt.files.length) {
        items = dt.files
      } else if (dt.items && dt.items.length) {
        items = dt.items
      }
    } else if (target && target.files) {
      items = target.files
    }
  }

  return Array.prototype.slice.call(items)
}

interface ImageUploadProps {
  value: string
  accept?: any
  loading?: boolean
  onChange: (value: Blob | null) => void
  disabled?: boolean
  fluid?: boolean
  defaultImage?: string
  croppable?: boolean
  style?: object
  customPlaceholder?: React.ReactNode
}

const ImageUpload = ({
  value = '',
  accept,
  loading,
  onChange,
  disabled,
  fluid,
  defaultImage,
  croppable,
  style = {},
  customPlaceholder,
}: ImageUploadProps) => {
  const { t } = useTranslation()

  const [imageBlob, setImageBlob] = useState<string | ArrayBuffer | null>(null)

  const [cropSrc, setCropSrc] = useState<string | ArrayBuffer | null>(null)

  const [modalOpen, setModalOpen] = useState(false)

  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    const updateBlob = async () => {
      if (!value) {
        setImageBlob(null)
        return
      }

      if (typeof value === 'string') {
        try {
          // Use the old public.hr-skyen.dk domain to prevent cors error
          const image = await fetch(
            value
              .replace('hr-images.hr-on.com', 'public.hr-skyen.dk')
              .replace(/\/[0-9]+\/null/, ''),
          )

          const blob = await image.blob()
          const reader = new FileReader()
          reader.onload = () => {
            setImageBlob(reader.result)
          }
          reader.readAsDataURL(blob)
        } catch (e) {
          console.log((e as Error).message)
          setImageBlob(null)
        }
      }
    }
    updateBlob()
  }, [value])

  const readBlob = (value: Blob) => {
    onChange(value)
    try {
      const reader = new FileReader()
      reader.readAsDataURL(value)
      reader.onload = () => {
        const base64 = reader.result
        if (base64 !== imageBlob) {
          setImageBlob(base64)
        }
      }
    } catch (e) {}
  }

  const onSelectFile = (file?: string) => {
    if (disabled) {
      return
    }

    setModalOpen(true)

    if (file) {
      if (typeof file === 'string') {
        setCropSrc(file)
        return
      }

      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setCropSrc(reader.result)
      })
      reader.readAsDataURL(file)
    }
  }

  const src = imageBlob || value

  if (typeof value === 'object' && !isNull(value)) {
    readBlob(value)
  }

  const cancelCrop = () => {
    setCropSrc(null)
    setModalOpen(false)
  }

  const submitCrop = async (croppedBlob: File | null) => {
    setModalOpen(false)

    if (!croppedBlob) {
      setCropSrc(null)
      setImageBlob(null)
      onChange(null)
      return
    }

    const { result } = await readFileAsync(croppedBlob)

    setCropSrc(null)
    setImageBlob(result)
    onChange(croppedBlob)
  }
  return (
    <ImageUploadWrapper
      loading={loading}
      disabled={disabled}
      dashedBorder={!src && !defaultImage}
      fluid={fluid}
      onDragLeave={() => setDragging(false)}
      onDragOver={(e: DragEvent) => {
        if (!disabled) {
          e.stopPropagation()
          e.preventDefault()
          setDragging(true)
        }
      }}
      onDrop={(e: DragEvent) => {
        if (!disabled) {
          e.preventDefault()
          onSelectFile(getFilesFromEvent(e)[0])
          setDragging(false)
        }
      }}
      style={{
        ...(style ?? {}),
        background: dragging ? '#e8e8e8' : undefined,
      }}>
      {modalOpen && (
        <CropModal
          src={cropSrc as string}
          defaultSrc={defaultImage}
          onCancel={cancelCrop}
          onSubmit={submitCrop}
          accept={accept}
          croppable={croppable}
        />
      )}

      {(src || defaultImage) && (
        <Image
          style={{
            marginBottom: 0,
          }}
          src={src || defaultImage}
        />
      )}

      {!src && !defaultImage && !customPlaceholder ? (
        <DropContainer
          fluid={fluid}
          disabled={disabled}
          onClick={() => onSelectFile()}>
          <UploadCloud01 />
          <DropLabel>{t('Click here to upload image')}</DropLabel>
        </DropContainer>
      ) : customPlaceholder && !src && !defaultImage ? (
        <div onClick={() => onSelectFile()}>{customPlaceholder}</div>
      ) : (
        !disabled && (
          <EditIconWrapper>
            <EditIcon
              onClick={() => onSelectFile(src as string)}
              title={t('Edit image')}
              popupPosition="right center"
              popupOffset={[0, 0]}
            />
          </EditIconWrapper>
        )
      )}
    </ImageUploadWrapper>
  )
}

export default ImageUpload
